/**
 * The Telegram Mini App side of the owner report (MD-0128), without Telegram's script:
 * Telegram puts the signed launch data into the URL fragment (`tgWebAppData`) and takes
 * events from the page through its webview bridge or, on the web, the parent frame.
 * https://core.telegram.org/bots/webapps (read 2026-10-17).
 */

interface TelegramWindow extends Window {
  TelegramWebviewProxy?: { postEvent: (eventType: string, eventData: string) => void };
}

export function readTelegramInitData(hash: string): string | null {
  return new URLSearchParams(hash.replace(/^#/, '')).get('tgWebAppData') || null;
}

/** The fragment without the signed launch data: the theme and version stay. */
export function withoutLaunchData(hash: string): string {
  const params = new URLSearchParams(hash.replace(/^#/, ''));
  if (!params.has('tgWebAppData')) return hash;
  params.delete('tgWebAppData');
  const rest = params.toString();
  return rest ? `#${rest}` : '';
}

function postTelegramEvent(eventType: string): void {
  const w = window as TelegramWindow;
  try {
    if (w.TelegramWebviewProxy) {
      w.TelegramWebviewProxy.postEvent(eventType, '');
    } else if (w.parent !== w) {
      w.parent.postMessage(
        JSON.stringify({ eventType, eventData: '' }),
        'https://web.telegram.org',
      );
    }
  } catch {
    // Outside Telegram there is nobody to tell.
  }
}

/** The page is drawn: Telegram may hide its loader and give the app the full height. */
export function announceTelegramMiniAppReady(): void {
  postTelegramEvent('web_app_ready');
  postTelegramEvent('web_app_expand');
}

const TOKEN_KEY = 'owner-report-viewer';

export interface StoredViewer {
  token: string;
  expiresAt: string;
}

export function readStoredViewer(now: Date): StoredViewer | null {
  try {
    const raw = window.sessionStorage.getItem(TOKEN_KEY);
    if (!raw) return null;
    const stored = JSON.parse(raw) as StoredViewer;
    return new Date(stored.expiresAt) > now ? stored : null;
  } catch {
    return null;
  }
}

export function storeViewer(viewer: StoredViewer | null): void {
  try {
    if (viewer) window.sessionStorage.setItem(TOKEN_KEY, JSON.stringify(viewer));
    else window.sessionStorage.removeItem(TOKEN_KEY);
  } catch {
    // Private mode: the token lives as long as the page.
  }
}

/** Telegram passes its theme in the fragment; a dark background means the owner reads in dark mode. */
export function telegramThemeIsDark(hash: string): boolean {
  try {
    const theme = JSON.parse(
      new URLSearchParams(hash.replace(/^#/, '')).get('tgWebAppThemeParams') ?? '{}',
    ) as {
      bg_color?: string;
    };
    const match = /^#?([0-9a-f]{6})$/i.exec(theme.bg_color ?? '');
    if (!match?.[1]) return false;
    const [r, g, b] = [0, 2, 4].map((i) => parseInt(match[1]!.slice(i, i + 2), 16));
    return 0.299 * (r ?? 255) + 0.587 * (g ?? 255) + 0.114 * (b ?? 255) < 128;
  } catch {
    return false;
  }
}
